"use client";

import React, { useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  Save,
  RotateCcw,
  User,
  GripVertical,
} from "lucide-react";
import { FormModal } from "@/components/ui/form-modal";
import { useFormModal } from "@/lib/hooks/forms/use-form-modal";
import { useToast } from "@/components/ui/toast";
import { NavigationItem } from "./types";
import { SubmenuEditor } from "./navigation-customization/SubmenuEditor";

interface NavigationCustomizationModalProps {
  navigationItems: NavigationItem[];
  visibleItems: NavigationItem[];
  hiddenItems: NavigationItem[];
  onSave: (items: NavigationItem[]) => void;
  onReset: () => void;
  onToggleVisibility: (itemId: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onMoveChildUp: (parentId: string, childId: string) => void;
  onMoveChildDown: (parentId: string, childId: string) => void;
  onToggleChildVisibility: (parentId: string, childId: string) => void;
  trigger?: React.ReactNode;
}

export function NavigationCustomizationModal({
  navigationItems,
  visibleItems,
  hiddenItems,
  onSave,
  onReset,
  onToggleVisibility,
  onMoveUp,
  onMoveDown,
  onMoveChildUp,
  onMoveChildDown,
  onToggleChildVisibility,
  trigger,
}: NavigationCustomizationModalProps) {
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  // Initialize form modal with draft management
  const { formManager, handleClose } = useFormModal({
    form: {
      watch: () => ({}),
      handleSubmit: () => Promise.resolve(),
      reset: () => {},
      setValue: () => {},
      getValues: () => ({}),
      register: () => ({}),
      unregister: () => {},
      clearErrors: () => {},
      setError: () => {},
      trigger: () => Promise.resolve(true),
      control: {} as any,
      setFocus: () => {},
      getFieldState: () => ({}) as any,
      formState: { isSubmitting: false, errors: {} },
    } as any,
    draftKey: "navigation-customization-draft",
    isOpen,
    onClose: () => setIsOpen(false),
    autoSaveDraft: false, // We'll handle draft manually
    clearDraftOnClose: true,
    clearDraftOnSuccess: true,
  });

  const handleSave = useCallback(async () => {
    try {
      formManager.dismissError();
      onSave(navigationItems);
      formManager.showSuccess();
      setTimeout(() => {
        handleClose();
      }, 1500);
      showToast({
        type: "success",
        title: "Navigation customized successfully!",
      });
    } catch (error: any) {
      formManager.showError(error);
      showToast({
        type: "error",
        title: "Failed to save navigation",
        description: error?.message || "An error occurred.",
      });
    }
  }, [navigationItems, onSave, formManager, handleClose, showToast]);

  const handleReset = useCallback(async () => {
    try {
      formManager.dismissError();
      onReset();
      formManager.showSuccess();
      setTimeout(() => {
        handleClose();
      }, 1500);
      showToast({ type: "success", title: "Navigation reset to default!" });
    } catch (error: any) {
      formManager.showError(error);
      showToast({
        type: "error",
        title: "Failed to reset navigation",
        description: error?.message || "An error occurred.",
      });
    }
  }, [onReset, formManager, handleClose, showToast]);

  return (
    <>
      {/* Trigger */}
      <div onClick={() => setIsOpen(true)}>
        {trigger || (
          <Button variant="ghost" size="sm" className="p-2" title="Customize navigation">
            <User className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Modal */}
      <FormModal
        isOpen={isOpen}
        onClose={handleClose}
        title="Customize Navigation"
        description="Reorganize and customize your navigation bar. Drag items to reorder or toggle visibility."
        formManager={formManager}
        showClearDraft={false}
        maxWidth="2xl"
      >
        <div className="space-y-6">
          {/* Visible Items */}
          <div>
            <h3 className="text-sm font-medium text-card-foreground mb-3 flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Visible Items ({visibleItems.length})
            </h3>
            <div className="space-y-2">
              {visibleItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={item.id} className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
                        {Icon && <Icon className="h-4 w-4" />}
                        <span className="font-medium">{item.name}</span>
                        {item.children && item.children.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {item.children.length} items
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onToggleVisibility(item.id)}
                          className="h-8 w-8 p-0"
                          title="Hide Item"
                        >
                          <EyeOff className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onMoveUp(index)}
                          disabled={index === 0}
                          className="h-8 w-8 p-0"
                          title="Move Up"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onMoveDown(index)}
                          disabled={index === visibleItems.length - 1}
                          className="h-8 w-8 p-0"
                          title="Move Down"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {item.children && item.children.length > 0 && (
                      <details className="mt-2 [&[open]_.submenu-caret]:rotate-180">
                        <summary className="flex items-center gap-1 cursor-pointer select-none text-xs font-medium text-muted-foreground hover:text-foreground list-none [&::-webkit-details-marker]:hidden">
                          <ChevronDown className="submenu-caret h-3.5 w-3.5 transition-transform" />
                          Reorder submenu &amp; choose what hides under “More”
                        </summary>
                        <SubmenuEditor
                          parentId={item.id}
                          items={item.children}
                          onMoveChildUp={onMoveChildUp}
                          onMoveChildDown={onMoveChildDown}
                          onToggleChildVisibility={onToggleChildVisibility}
                        />
                      </details>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Hidden Items */}
          {hiddenItems.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-card-foreground mb-3 flex items-center gap-2">
                <EyeOff className="h-4 w-4" />
                Hidden Items ({hiddenItems.length})
              </h3>
              <div className="space-y-2">
                {hiddenItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <Card key={item.id} className="p-3 opacity-60">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
                          {Icon && <Icon className="h-4 w-4" />}
                          <span className="font-medium">{item.name}</span>
                          {item.children && item.children.length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {item.children.length} items
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onToggleVisibility(item.id)}
                            className="h-8 w-8 p-0"
                            title="Show Item"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset to Default
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </FormModal>
    </>
  );
}
